import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, HorizontalBars, InfoTooltip } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';

export const Index_TargetStakeholderFragment = gql(/* GraphQL */ `
  fragment Index_TargetStakeholderFragment on HealthcareIndicators {
    targetStakeholder {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type TargetStakeholderProps = {
  className?: string;
  indicators: FragmentType<typeof Index_TargetStakeholderFragment>;
};

export function TargetStakeholder({ indicators, className }: TargetStakeholderProps) {
  const { targetStakeholder } = useFragment(Index_TargetStakeholderFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip } =
    useChartTranslations('targetStakeholder');

  const sortedItems = useMemo(() => {
    const colors = generateChartColors(targetStakeholder.items.length);
    const sorted = orderBy(targetStakeholder.items, ['pct'], ['desc']);
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [targetStakeholder.items]);

  const min = Math.min(0, ...targetStakeholder.items.map((item) => item?.count ?? 0));
  const max = Math.max(...targetStakeholder.items.map((item) => item?.count ?? 0));

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip>{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={targetStakeholder.isDataSuppressed}>
        <HorizontalBars min={min} max={max} showInlineDisplayValue={false}>
          {sortedItems.map((item, i) => {
            if (!item) return null;
            return (
              <HorizontalBars.Item
                key={i}
                label={getOptionLabel(item.label)}
                tooltipLabel={getOptionTooltip(item.label)}
                value={item.count ?? null}
                displayValue={item.count?.toLocaleString() ?? ''}
                color={item.color}
              />
            );
          })}
        </HorizontalBars>
      </ChartCard.Body>
    </ChartCard>
  );
}
